import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import {
  onecImportCreateProfile,
  onecImportDeleteProfile,
  onecImportParse,
  onecImportUpdateProfile,
  onecImportValidate,
} from '../api/importApi'
import {
  DEFAULT_PROFILE_FILTER,
  EMPTY_MAPPING_DRAFT,
  EMPTY_UPLOAD_PARAMS,
  type CommandAttempt,
  type ImportIntentState,
  type ImportProfile,
  type InvoiceColumnRole,
  type MappingDraft,
  type ProfileColumnDraft,
  type ProfileDraft,
  type ProfileFilter,
  type UploadParams,
} from './types'
import type { AttemptKind } from './constants'

/**
 * Намерение человека в импорте накладных: фильтр профилей, параметры загрузки,
 * форма профиля, разметка колонок и ключи попыток команд. Ответов сервера здесь
 * НЕТ (R01). Каждый ключ принадлежит rid; сам файл накладной в состояние не
 * попадает (`File` не сериализуется). Срок жизни — `screen`.
 */
const initialState: ImportIntentState = {
  profileFilters: {},
  uploadParams: {},
  uploadAttempts: {},
  profileEditors: {},
  mappings: {},
  attempts: {},
}

export const attemptKey = (kind: AttemptKind, resourceId: string, entity: string) => `${kind}:${resourceId}:${entity}`
export const mappingKey = (resourceId: string, fileId: string) => `${resourceId}:${fileId}`

export function emptyProfileDraft(): ProfileDraft {
  return {
    id: null,
    name: '',
    supplierName: '',
    sheetNameHint: '',
    headerRowIndex: '',
    defaultUnit: '',
    defaultVatRate: '',
    priceIncludesVat: false,
    quantityInPackages: false,
    columns: [{ role: 'Name', headerText: '', columnIndex: '' }],
  }
}

export function profileToDraft(profile: ImportProfile): ProfileDraft {
  return {
    id: profile.id,
    name: profile.name,
    supplierName: profile.supplierName ?? '',
    sheetNameHint: profile.sheetNameHint ?? '',
    headerRowIndex: profile.headerRowIndex === null ? '' : String(profile.headerRowIndex),
    defaultUnit: profile.defaultUnit ?? '',
    defaultVatRate: profile.defaultVatRate ?? '',
    priceIncludesVat: profile.priceIncludesVat,
    quantityInPackages: profile.quantityInPackages,
    columns: profile.columns.map((column) => ({
      role: column.role,
      headerText: column.headerText ?? '',
      columnIndex: column.columnIndex === null ? '' : String(column.columnIndex),
    })),
  }
}

function dropByResource<T>(record: Record<string, T>, resourceId: string): void {
  for (const key of Object.keys(record)) {
    if (key === resourceId || key.startsWith(`${resourceId}:`) || key.split(':')[1] === resourceId) delete record[key]
  }
}

function mappingOf(state: ImportIntentState, key: string): MappingDraft {
  const found = state.mappings[key]
  if (found !== undefined) return found
  const created: MappingDraft = { ...EMPTY_MAPPING_DRAFT, roles: {} }
  state.mappings[key] = created
  return created
}

type EditorPatch = Partial<Omit<ProfileDraft, 'columns' | 'id'>>

const slice = createSlice({
  name: 'onec/import',
  initialState,
  reducers: {
    profileFilterChanged(state, action: PayloadAction<{ resourceId: string; patch: Partial<ProfileFilter> }>) {
      const { resourceId, patch } = action.payload
      state.profileFilters[resourceId] = { ...(state.profileFilters[resourceId] ?? DEFAULT_PROFILE_FILTER), ...patch }
    },
    uploadParamsChanged(state, action: PayloadAction<{ resourceId: string; patch: Partial<UploadParams> }>) {
      const { resourceId, patch } = action.payload
      state.uploadParams[resourceId] = { ...(state.uploadParams[resourceId] ?? EMPTY_UPLOAD_PARAMS), ...patch }
    },
    /** Ключи загрузки сохраняются на отпечаток (файл+параметры): тот же файл после обрыва — те же ключи. */
    uploadAttemptRecorded(
      state,
      action: PayloadAction<{ resourceId: string; fingerprint: string; attempt: CommandAttempt }>,
    ) {
      const { resourceId, fingerprint, attempt } = action.payload
      const known = state.uploadAttempts[resourceId]
      if (known === undefined || known.fingerprint !== fingerprint) {
        state.uploadAttempts[resourceId] = { fingerprint, attempt }
      }
    },
    profileEditorOpened(state, action: PayloadAction<{ resourceId: string; draft: ProfileDraft }>) {
      state.profileEditors[action.payload.resourceId] = action.payload.draft
    },
    profileEditorClosed(state, action: PayloadAction<{ resourceId: string }>) {
      // Ключи попытки остаются: исход записи мог быть неизвестен, и повтор того же ввода пойдёт с ними.
      state.profileEditors[action.payload.resourceId] = null
    },
    profileEditorChanged(state, action: PayloadAction<{ resourceId: string; patch: EditorPatch }>) {
      const { resourceId, patch } = action.payload
      const draft = state.profileEditors[resourceId]
      if (draft === null || draft === undefined) return
      state.profileEditors[resourceId] = { ...draft, ...patch }
      delete state.attempts[attemptKey('profile', resourceId, draft.id ?? 'new')]
    },
    profileColumnChanged(
      state,
      action: PayloadAction<{ resourceId: string; index: number; patch: Partial<ProfileColumnDraft> }>,
    ) {
      const { resourceId, index, patch } = action.payload
      const draft = state.profileEditors[resourceId]
      if (draft === null || draft === undefined || draft.columns[index] === undefined) return
      const columns = draft.columns.map((column, at) => (at === index ? { ...column, ...patch } : column))
      state.profileEditors[resourceId] = { ...draft, columns }
      delete state.attempts[attemptKey('profile', resourceId, draft.id ?? 'new')]
    },
    profileColumnAdded(state, action: PayloadAction<{ resourceId: string }>) {
      const { resourceId } = action.payload
      const draft = state.profileEditors[resourceId]
      if (draft === null || draft === undefined) return
      const next: ProfileColumnDraft = { role: 'Sku', headerText: '', columnIndex: '' }
      state.profileEditors[resourceId] = { ...draft, columns: [...draft.columns, next] }
      delete state.attempts[attemptKey('profile', resourceId, draft.id ?? 'new')]
    },
    profileColumnRemoved(state, action: PayloadAction<{ resourceId: string; index: number }>) {
      const { resourceId, index } = action.payload
      const draft = state.profileEditors[resourceId]
      if (draft === null || draft === undefined) return
      state.profileEditors[resourceId] = { ...draft, columns: draft.columns.filter((_, at) => at !== index) }
      delete state.attempts[attemptKey('profile', resourceId, draft.id ?? 'new')]
    },
    columnRoleChanged(
      state,
      action: PayloadAction<{ resourceId: string; fileId: string; columnIndex: number; role: InvoiceColumnRole }>,
    ) {
      const { resourceId, fileId, columnIndex, role } = action.payload
      mappingOf(state, mappingKey(resourceId, fileId)).roles[String(columnIndex)] = role
      delete state.attempts[attemptKey('mapping', resourceId, fileId)]
    },
    mappingChanged(
      state,
      action: PayloadAction<{ resourceId: string; fileId: string; patch: Partial<Omit<MappingDraft, 'roles'>> }>,
    ) {
      const { resourceId, fileId, patch } = action.payload
      Object.assign(mappingOf(state, mappingKey(resourceId, fileId)), patch)
      delete state.attempts[attemptKey('mapping', resourceId, fileId)]
    },
    /** Ключи записаны ДО отправки: повтор после обрыва уйдёт с теми же, и второй эффект не создастся (ADR-11). */
    attemptRecorded(state, action: PayloadAction<{ key: string; attempt: CommandAttempt }>) {
      state.attempts[action.payload.key] ??= action.payload.attempt
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(userScopeCleared, () => initialState)
      .addCase(resourceScopeCleared, (state, action) => {
        const { resourceId } = action.payload
        dropByResource(state.profileFilters, resourceId)
        dropByResource(state.uploadParams, resourceId)
        dropByResource(state.uploadAttempts, resourceId)
        dropByResource(state.profileEditors, resourceId)
        dropByResource(state.mappings, resourceId)
        dropByResource(state.attempts, resourceId)
      })
      .addMatcher(onecImportParse.matchFulfilled, (state, action) => {
        const args = action.meta.arg.originalArgs
        if (args.kind === 'upload') delete state.uploadAttempts[args.resourceId]
        else delete state.attempts[attemptKey('mapping', args.resourceId, args.fileId)]
      })
      .addMatcher(onecImportValidate.matchFulfilled, (state, action) => {
        const { resourceId, fileId } = action.meta.arg.originalArgs
        delete state.attempts[attemptKey('validate', resourceId, fileId)]
      })
      .addMatcher(onecImportCreateProfile.matchFulfilled, (state, action) => {
        const { resourceId } = action.meta.arg.originalArgs
        delete state.attempts[attemptKey('profile', resourceId, 'new')]
        state.profileEditors[resourceId] = null
      })
      .addMatcher(onecImportUpdateProfile.matchFulfilled, (state, action) => {
        const { resourceId, profileId } = action.meta.arg.originalArgs
        delete state.attempts[attemptKey('profile', resourceId, profileId)]
        state.profileEditors[resourceId] = null
      })
      .addMatcher(onecImportDeleteProfile.matchFulfilled, (state, action) => {
        const { resourceId, profileId } = action.meta.arg.originalArgs
        delete state.attempts[attemptKey('delete', resourceId, profileId)]
      })
  },
})

export const {
  profileFilterChanged,
  uploadParamsChanged,
  uploadAttemptRecorded,
  profileEditorOpened,
  profileEditorClosed,
  profileEditorChanged,
  profileColumnChanged,
  profileColumnAdded,
  profileColumnRemoved,
  columnRoleChanged,
  mappingChanged,
  attemptRecorded,
} = slice.actions
export const importReducer = slice.reducer
